import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  designTokens,
  notificationSeverityTokens,
  type NotificationSeverityName,
} from "@/lib/tokens";

export const ToastProvider = ToastPrimitives.Provider;

export const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      // High z-index so notifications sit above modals/overlays across the app.
      "pointer-events-none fixed inset-x-4 top-4 z-[9999] flex max-w-[420px] flex-col gap-3 sm:inset-x-auto sm:right-4",
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

interface ToastLayoutProps
  extends Omit<React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root>, "title"> {
  tone: NotificationSeverityName;
  icon?: React.ReactNode | null;
  title?: React.ReactNode;
  description?: React.ReactNode;
  dismissible?: boolean;
  actionSlot?: React.ReactNode;
  progress?: number | null;
  autoProgress?: boolean;
  durationMs: number;
  onDismiss?: () => void;
}

const FOCUS_RING = `focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[${designTokens.accentRule.brand}]`;

const usePrefersReducedMotion = () => {
  const [prefers, setPrefers] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const listener = () => setPrefers(media.matches);
    listener();
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, []);

  return prefers;
};

export const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  ToastLayoutProps
>((
  {
    tone,
    icon,
    title,
    description,
    dismissible = true,
    actionSlot,
    progress,
    autoProgress,
    durationMs,
    onDismiss,
    className,
    ...props
  },
  ref,
) => {
  const toneTokens = notificationSeverityTokens[tone];
  const accentColor = tone === "critical" ? designTokens.accentRule.brand : toneTokens.accent;
  const prefersReducedMotion = usePrefersReducedMotion();
  const showAutoProgress = autoProgress && !prefersReducedMotion;
  const isAssertive = tone === "error" || tone === "critical";

  return (
    <ToastPrimitives.Root
      ref={ref}
      aria-live={isAssertive ? "assertive" : "polite"}
      duration={durationMs}
      className={cn(
        "pointer-events-auto relative flex w-full overflow-hidden rounded-md border shadow-lg transition-all",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-80 data-[state=open]:slide-in-from-top-sm",
        "data-[state=closed]:slide-out-to-right-sm",
        prefersReducedMotion && "data-[state=open]:opacity-100 data-[state=open]:translate-y-0 data-[state=closed]:opacity-0",
        className,
      )}
      style={{
        backgroundColor: toneTokens.surface,
        borderColor: toneTokens.border,
        color: toneTokens.text,
        borderLeftWidth: "4px",
        borderLeftColor: accentColor,
      }}
      onOpenChange={(open) => {
        if (!open) {
          onDismiss?.();
        }
      }}
      {...props}
    >
      <div className="flex w-full gap-3" style={{ padding: designTokens.spacing.rem.lg }}>
        <div
          className="flex h-5 w-5 items-center justify-center text-current"
          style={{
            color: accentColor,
            width: "20px",
            height: "20px",
          }}
        >
          {icon}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {title ? (
            <div className="text-sm font-semibold leading-snug text-slate-900">
              {title}
            </div>
          ) : null}
          {description ? (
            <div className="text-sm leading-relaxed text-slate-700">{description}</div>
          ) : null}
          {(typeof progress === "number" || showAutoProgress) && (
            <div
              className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/30"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={1}
              aria-valuenow={progress ?? undefined}
            >
              <div
                className={cn(
                  "h-full bg-current transition-all",
                  showAutoProgress && "toast-auto-progress",
                )}
                style={{
                  backgroundColor: accentColor,
                  width:
                    typeof progress === "number"
                      ? `${Math.round(progress * 100)}%`
                      : "100%",
                  animationDuration: showAutoProgress ? `${durationMs}ms` : undefined,
                }}
              />
            </div>
          )}
        </div>
        <div className="ml-3 flex shrink-0 items-start gap-2">
          {actionSlot}
          {dismissible ? <ToastClose tone={tone} /> : null}
        </div>
      </div>
    </ToastPrimitives.Root>
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

interface ToastCloseProps extends React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close> {
  tone: NotificationSeverityName;
}

export const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  ToastCloseProps
>(({ tone, className, ...props }, ref) => {
  const accentColor = tone === "critical" ? designTokens.accentRule.brand : notificationSeverityTokens[tone].accent;
  return (
    <ToastPrimitives.Close
      ref={ref}
      aria-label="Dismiss notification"
      className={cn(
        "flex h-6 w-6 items-center justify-center rounded-full text-slate-500 transition-colors hover:text-slate-900",
        FOCUS_RING,
        className,
      )}
      style={{ outlineColor: accentColor }}
      toast-close=""
      {...props}
    >
      <X aria-hidden="true" className="h-3.5 w-3.5" />
    </ToastPrimitives.Close>
  );
});
ToastClose.displayName = ToastPrimitives.Close.displayName;

export const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "pointer-events-auto inline-flex h-8 items-center justify-center rounded-md border bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50",
      FOCUS_RING,
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

export const ToastTitle: React.FC<{ children?: React.ReactNode }> = ({ children }) => <>{children}</>;

export const ToastDescription: React.FC<{ children?: React.ReactNode }> = ({ children }) => <>{children}</>;

export type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;
